<template>
  <ErrorRetrievingData v-if="errorFetchingDocuments" document-type="invoices" class="min-h-[560px]" />

  <Stack v-else direction="col" gap="xs" class="w-full pt-xs min-h-[560px]">
    <Stack class="px-xs md:px-none">
      <Heading size="sm">Your invoices</Heading>
    </Stack>

    <InvoicesSearch />

    <NoDocuments v-if="displayNoDocuments" document-type="invoices" class="w-full min-h-[560px]" />

    <Stack v-if="displayResults" direction="col" gap="md" class="w-full mt-2xs">
      <Stack
        direction="col"
        align="center"
        justify="between"
        class="w-full"
        :breakpoints="{ direction: { sm: 'row' } }"
      >
        <Stack align="center" gap="2xs" class="w-full px-xs md:px-none flex-wrap">
          <InvoicesSelector
            :selected="documentsSelected"
            :indeterminate="!selectedAll"
            @select:all="(value) => selectAllDocuments(value)"
            @select:paid="selectPaidDocuments"
            @select:unpaid="selectUnpaidDocuments"
          />
          <Stack gap="2xs" data-fs="invoiceDownloadPay">
            <Button
              variant="ghost"
              size="md"
              :disabled="selectedDocuments.length === 0 || downloadingDocuments"
              @click="handleMultipleDownload"
              class="!w-auto"
              data-test-id="downloadBtn"
            >
              <SpinnerLoader v-if="downloadingDocuments" size="md" />
              <Icon v-else name="download" :size="14" :color="getColor('blue-dark')" />
              <span>Download</span>
            </Button>
            <Button
              size="md"
              :disabled="!canPay || downloadingDocuments"
              @click="paymentHandler"
              class="!w-auto"
              data-fs="payInvoiceButton"
              data-test-id="payBtn"
            >
              <Icon name="credit-card-icon" :size="16" />
              <span>Pay</span>
            </Button>
          </Stack>
        </Stack>
        <ItemsPerPageSelector
          v-if="documentsSearchResult"
          :showItemsPerPageMenu="documentsSearchResult.length > 10"
          document-type="invoices"
          class="px-xs md:px-none"
          :from="fromItem"
          :to="toItem"
          :total="documentsSearchResult.length"
          @update:items="(value) => updateItemsPerPage(value)"
        />
      </Stack>

      <Table
        v-if="documentsSearchResult"
        :row-selector="true"
        :fields="invoicesTableFields"
        :data="documentsSearchResult"
        :page="page"
        :items-per-page="itemsPerPage"
        row-data-test-id="invoiceLine"
        sortable
        @row-click="(item: any) => handleSingleDocumentView(item as FormattedDocument)"
        @row-selected="(item) => updateSelectedDocuments(item as FormattedDocument)"
      >
        <template #status="{ item }">
          <Pill :text="item.status" :variant="getStatusVariant(item as FormattedDocument)" />
        </template>

        <template #actions="{ item }">
          <SpinnerLoader v-if="isLoading(item.document_number)" size="md" class="!justify-end md:px-sm" />
          <InvoiceActionsMenu
            v-else
            :document="item as FormattedDocument"
            @download="(document) => handleSingleDocumentDownload(document)"
            @view="(document) => handleSingleDocumentView(document)"
            @pay="(document) => handleSingleDocumentPay(document)"
          />
        </template>

        <template #pagination>
          <Pagination
            v-if="displayResults"
            class="my-lg"
            :items="pagesNumber"
            :page="page"
            :buttons="3"
            :per-page="itemsPerPage"
            @update:page="(value) => updatePage(value)"
          />
        </template>
      </Table>
    </Stack>

    <Stack class="w-full px-xs md:px-none">
      <Stack direction="col" class="w-full px-sm border-xl border-mkm-blue-default">
        <br-component component="main">
          <template #default="{ component }">
            <br-component :component="component" />
          </template>
        </br-component>
      </Stack>
    </Stack>
  </Stack>
</template>

<script lang="ts" setup>
import type { FormattedDocument } from "~/composables/useDocuments/useDocumentsTypes";
import { downloadFromBase64, getColor, viewFromBase64 } from "mkm-avengers";
import { ROUTES } from "~/helpers/routes/routes";

const invoicesTableFields = [
  { key: "document_number", label: "Invoice No.", testId: "invoiceNumber" },
  { key: "document_type", label: "Type", testId: "typeField" },
  { key: "document_date", label: "Issued", testId: "issuedDateField" },
  { key: "document_due", label: "Due", testId: "dueDateField" },
  { key: "value", label: "Value", testId: "valueField" },
  { key: "outstanding", label: "Balance", testId: "balanceField" },
  { key: "customer_reference", label: "Job Reference", testId: "jobReference" },
  { key: "status", label: "Status", testId: "statusField" },
];

const {
  documentsSearchResult,
  errorFetchingDocuments,
  selectedDocuments,
  fetchDocument,
  getStatusVariant,
  selectAllDocuments,
  selectPaidDocuments,
  selectUnpaidDocuments,
  updateSelectedDocuments,
  PayableStatus,
  BillableStatus,
} = useDocuments();

const { setDocumentsToPay, setBalanceToPay } = usePayments();

const router = useRouter();

const displayResults = computed(
  () =>
    Array.isArray(documentsSearchResult.value) &&
    documentsSearchResult.value.length > 0 &&
    !errorFetchingDocuments.value,
);

const displayNoDocuments = computed(
  () =>
    Array.isArray(documentsSearchResult.value) &&
    documentsSearchResult.value.length === 0 &&
    !errorFetchingDocuments.value,
);

const documentsSelected = computed(() => selectedDocuments.value.length > 0);

const selectedAll = computed(
  () =>
    Array.isArray(documentsSearchResult.value) && documentsSearchResult.value.length === selectedDocuments.value.length,
);

const canPay = computed(() => {
  const totalOutstanding = selectedDocuments.value.reduce(
    (acc, doc) => acc + Number.parseFloat(doc.outstanding.replace(/[^\d.-]+/g, "")),
    0,
  );

  const hasOutstandingInvoice = selectedDocuments.value.some((document) => BillableStatus.includes(document.status));

  return hasOutstandingInvoice && totalOutstanding > 0;
});

const loadingDocument = ref<number | null>(null);
const downloadingDocuments = ref<boolean>(false);

const isLoading = (documentNumber: number): boolean => loadingDocument.value === documentNumber;

const page = ref<number>(1);
const itemsPerPage = ref<number>(10);

const pagesNumber = ref<number>(
  documentsSearchResult.value ? Math.ceil(documentsSearchResult.value.length / itemsPerPage.value) : 0,
);

const fromItem = computed(() => (page.value - 1) * itemsPerPage.value + 1);
const toItem = computed(() => Math.min(page.value * itemsPerPage.value, documentsSearchResult.value?.length || 0));

const updatePage = (value: number): void => {
  page.value = value;
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const updateItemsPerPage = (value: number): void => {
  itemsPerPage.value = value;
};

const updatePagesNumber = (value: number): void => {
  pagesNumber.value = value;
};

const handleSingleDocumentDownload = async (document: FormattedDocument): Promise<void> => {
  if (isLoading(document.document_number)) return;

  loadingDocument.value = document.document_number;

  const documentData = await fetchDocument({
    year: document.year,
    period: document.period,
    number: document.glitem,
  });

  if (!documentData) return;

  downloadFromBase64(documentData.document, documentData.name);

  loadingDocument.value = null;
};

const handleSingleDocumentView = async (document: FormattedDocument): Promise<void> => {
  if (isLoading(document.document_number)) return;

  loadingDocument.value = document.document_number;

  const documentData = await fetchDocument({
    year: document.year,
    period: document.period,
    number: document.glitem,
  });

  if (!documentData) return;

  viewFromBase64(documentData.document);

  loadingDocument.value = null;
};

const handleSingleDocumentPay = (document: FormattedDocument): void => {
  setBalanceToPay(0);
  setDocumentsToPay([document]);

  router.push({ path: ROUTES.PAY });
};

const handleMultipleDownload = async (): Promise<void> => {
  if (downloadingDocuments.value) return;

  downloadingDocuments.value = true;

  for (const document of selectedDocuments.value) {
    const documentData = await fetchDocument({
      year: document.year,
      period: document.period,
      number: document.glitem,
    });

    if (documentData) {
      await downloadFromBase64(documentData.document, documentData.name);
    }
  }

  downloadingDocuments.value = false;
};

const paymentHandler = () => {
  let documentsToPay: FormattedDocument[] = [];

  selectedDocuments.value.forEach((document: FormattedDocument) => {
    if (PayableStatus.includes(document.status)) {
      documentsToPay.push(document);
    }
  });
  setBalanceToPay(0);
  setDocumentsToPay(documentsToPay);

  router.push({ path: ROUTES.PAY });
};

watch([documentsSearchResult, itemsPerPage], async ([newDocumentsSearchResult, newItemsPerPage]) => {
  updatePagesNumber(newDocumentsSearchResult ? Math.ceil(newDocumentsSearchResult.length / newItemsPerPage) : 0);
  updatePage(1);
});
</script>
